import React from 'react';
import { FieldCheckBox, FieldInput, FormCol, FormRow } from 'motion-components';
import { Table } from 'react-bootstrap';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { formatDateSafe, formatPrice, priceCalculationMethodLabel } from '../item-price';
import {
  FieldSelectItemGroups,
  FieldSelectItemSubgroups,
  FieldSelectPriceCalculationMethod,
} from './field-selects';
import { FormCol6 } from 'motion-components';

/** Πεδία είδους. Οι τιμές μένουν ιστορικό και δεν αποθηκεύονται από εδώ. */
export const FormFields = ({ data, canEdit }) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<any>();

  const prices = [...(data?.itemPrices ?? [])].sort((a, b) =>
    String(b.fromDate ?? '').localeCompare(String(a.fromDate ?? '')),
  );

  // Η υποομάδα ανήκει πάντα στην επιλεγμένη ομάδα
  const subgroupWhere = values.groupId
    ? JSON.stringify({ groupId: values.groupId })
    : JSON.stringify({ id: -1 });

  return (
    <>
      <FormRow>
        <FieldInput disabled={!canEdit} as={FormCol} label={t('Κωδικός')} name="code" />
        <FieldInput required disabled={!canEdit} as={FormCol6} label={t('Είδος')} name="name" />
        <FieldCheckBox
          disabled={!canEdit}
          as={FormCol}
          label={t('Ενεργή Εγγραφή')}
          name="isActive"
          onLabel={t('ΝΑΙ')}
          offLabel={t('ΟΧΙ')}
        />
      </FormRow>
      <FormRow>
        <FormCol />
        <FieldInput disabled={!canEdit} as={FormCol6} label={t('Είδος (EN)')} name="enName" />
      </FormRow>
      <FormRow>
        <FieldInput disabled={!canEdit} as={FormCol} label={t('Πλήρες όνομα')} name="fullName" />
        <FieldInput disabled={!canEdit} as={FormCol} label={t('Υποσημείωση')} name="footnote" />
        <FieldInput
          disabled={!canEdit}
          as={FormCol}
          label={t('Υποσημείωση (EN)')}
          name="enFootnote"
        />
      </FormRow>
      <FormRow>
        <FieldSelectItemGroups
          disabled={!canEdit}
          as={FormCol}
          label={t('Ομάδα')}
          name="groupId"
          sbLabel={data?.group?.id === values.groupId ? data.group.name : ''}
          onChange={(option) => {
            const nextId = (option as any)?.value ?? null;
            if (nextId !== values.groupId) setFieldValue('subgroupId', null);
          }}
        />
        <FieldSelectItemSubgroups
          disabled={!canEdit}
          as={FormCol}
          label={t('Υποομάδα')}
          name="subgroupId"
          where={subgroupWhere}
          sbLabel={data?.subgroup?.id === values.subgroupId ? data.subgroup.name : ''}
        />
        <FieldSelectPriceCalculationMethod
          disabled={!canEdit}
          label={t('Υπολογισμός τιμής')}
          name="priceCalculationMethod"
        />
        <FieldCheckBox
          disabled={!canEdit}
          as={FormCol}
          label={t('Αναλυτικά')}
          name="hasDetails"
          onLabel={t('ΝΑΙ')}
          offLabel={t('ΟΧΙ')}
        />
      </FormRow>
      <FormRow>
        <FieldInput disabled={!canEdit} as={FormCol} label={t('Μονάδα 1')} name="units1" />
        <FieldInput disabled={!canEdit} as={FormCol} label={t('Μονάδα 2')} name="units2" />
        <FieldInput disabled={!canEdit} as={FormCol} label={t('Μονάδα 3')} name="units3" />
      </FormRow>
      <FormRow>
        <FieldCheckBox
          disabled={!canEdit}
          as={FormCol}
          label={t('ΕΔΟΕ')}
          name="useInEdoe"
          onLabel={t('ΝΑΙ')}
          offLabel={t('ΟΧΙ')}
        />
        <FieldCheckBox
          disabled={!canEdit}
          as={FormCol}
          label={t('ΕΔΟΕ μεταχειρισμένα')}
          name="useInEdoeUsed"
          onLabel={t('ΝΑΙ')}
          offLabel={t('ΟΧΙ')}
        />
        <FieldCheckBox
          disabled={!canEdit}
          as={FormCol}
          label={t('εκτός ΕΔΟΕ')}
          name="useInNonEdoe"
          onLabel={t('ΝΑΙ')}
          offLabel={t('ΟΧΙ')}
        />
      </FormRow>
      <FormRow>
        <FieldCheckBox
          disabled={!canEdit}
          as={FormCol}
          label={t('ΕΜΠΑ')}
          name="useInEmpa"
          onLabel={t('ΝΑΙ')}
          offLabel={t('ΟΧΙ')}
        />
        <FieldInput
          disabled={!canEdit}
          as={FormCol}
          label={t('Κατηγορία ΕΜΠΑ')}
          name="empaCategory"
        />
        <FieldInput
          disabled={!canEdit}
          as={FormCol}
          label={t('Κατηγορία ΕΜΠΑ (EN)')}
          name="enEmpaCategory"
        />
      </FormRow>
      <FormRow>
        <FieldInput disabled={!canEdit} as={FormCol} label={t('Κωδικός ERP')} name="erpItemCode" />
        <FieldInput
          disabled={!canEdit}
          as={FormCol6}
          label={t('Ονομασία ERP')}
          name="erpItemName"
        />
      </FormRow>

      <h5 className="mt-4">{t('Ιστορικό τιμών')}</h5>
      <Table striped bordered hover size="sm" className="mt-2">
        <thead>
          <tr>
            <th>{t('Ισχύς από')}</th>
            <th className="text-end">{t('Τιμή')}</th>
            <th>{t('Υπολογισμός τιμής')}</th>
            <th className="text-center">{t('Ενεργή')}</th>
          </tr>
        </thead>
        <tbody>
          {prices.length === 0 && (
            <tr>
              <td colSpan={4} className="text-center text-muted">
                {t('Δεν υπάρχουν τιμές για αυτό το είδος')}
              </td>
            </tr>
          )}
          {prices.map((price) => (
            <tr key={price.id}>
              <td>{formatDateSafe(price.fromDate)}</td>
              <td className="text-end">{formatPrice(price.price)}</td>
              <td>
                {priceCalculationMethodLabel(
                  price.priceCalculationMethod ?? data?.priceCalculationMethod,
                  t,
                )}
              </td>
              <td className="text-center">{price.isActive === false ? t('ΟΧΙ') : t('ΝΑΙ')}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
};
